import { supabase } from '@/integrations/supabase/client';
import type { 
  PersonalTemplate, 
  PersonalTemplateListItem, 
  SaveTemplateParams 
} from '@/types/supremeIntelligenceTypes';

const STORAGE_KEY = 'personal_templates';

// Local storage helpers
const getLocalTemplates = (): PersonalTemplate[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveLocalTemplates = (templates: PersonalTemplate[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch (error) {
    console.error('Failed to save templates to localStorage:', error);
  }
};

export const saveTemplate = async (params: SaveTemplateParams): Promise<PersonalTemplate> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  const template: PersonalTemplate = {
    id: crypto.randomUUID(),
    userId: user?.id || null,
    name: params.name.trim(),
    description: params.description?.trim(),
    category: params.category,
    icon: params.icon || 'Star',
    scenarios: params.scenarios,
    isPublic: params.isPublic || false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  if (user) {
    // Save to Supabase
    const { data, error } = await supabase
      .from('personal_templates')
      .insert({
        name: template.name,
        description: template.description,
        category: template.category,
        icon: template.icon,
        scenarios: template.scenarios as any,
        is_public: template.isPublic
      })
      .select()
      .single();

    if (error) throw error;
    
    return {
      id: data.id,
      userId: data.user_id,
      name: data.name,
      description: data.description,
      category: data.category as PersonalTemplate['category'],
      icon: data.icon,
      scenarios: data.scenarios as any,
      isPublic: data.is_public,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } else {
    // Save to localStorage
    const localTemplates = getLocalTemplates();
    localTemplates.push(template);
    saveLocalTemplates(localTemplates);
    return template;
  }
};

export const loadTemplate = async (id: string): Promise<PersonalTemplate | null> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (user) {
    const { data, error } = await supabase
      .from('personal_templates')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Failed to load template from Supabase:', error);
      return null;
    }

    if (data) {
      // Update last accessed
      await supabase
        .from('personal_templates')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', id);

      return {
        id: data.id,
        userId: data.user_id,
        name: data.name,
        description: data.description,
        category: data.category as PersonalTemplate['category'],
        icon: data.icon,
        scenarios: data.scenarios as any,
        isPublic: data.is_public,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    }
  }

  // Fallback to localStorage
  const localTemplates = getLocalTemplates();
  return localTemplates.find(t => t.id === id) || null;
};

export const listUserTemplates = async (): Promise<PersonalTemplateListItem[]> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (user) {
    const { data, error } = await supabase
      .from('personal_templates')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });

    if (error) {
      console.error('Failed to list templates from Supabase:', error);
      return [];
    }

    return (data || []).map(t => ({
      id: t.id,
      name: t.name,
      description: t.description,
      category: t.category,
      icon: t.icon,
      scenarioCount: Array.isArray(t.scenarios) ? t.scenarios.length : 0,
      createdAt: t.created_at,
      updatedAt: t.updated_at
    }));
  }

  // Fallback to localStorage
  const localTemplates = getLocalTemplates();
  return localTemplates.map(t => ({
    id: t.id,
    name: t.name,
    description: t.description,
    category: t.category,
    icon: t.icon,
    scenarioCount: t.scenarios.length,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt
  }));
};

export const deleteTemplate = async (id: string): Promise<boolean> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (user) {
    const { error } = await supabase
      .from('personal_templates')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (error) {
      console.error('Failed to delete template from Supabase:', error);
      return false;
    }
    return true;
  }

  // Delete from localStorage
  const localTemplates = getLocalTemplates();
  const filtered = localTemplates.filter(t => t.id !== id);
  saveLocalTemplates(filtered);
  return true;
};

export const updateTemplate = async (
  id: string,
  updates: Partial<SaveTemplateParams>
): Promise<PersonalTemplate | null> => {
  const { data: { user } } = await supabase.auth.getUser();

  if (user) {
    const updateData: any = {};
    if (updates.name) updateData.name = updates.name.trim();
    if (updates.description !== undefined) updateData.description = updates.description?.trim();
    if (updates.category) updateData.category = updates.category;
    if (updates.icon) updateData.icon = updates.icon;
    if (updates.scenarios) updateData.scenarios = updates.scenarios as any;
    if (updates.isPublic !== undefined) updateData.is_public = updates.isPublic;

    const { data, error } = await supabase
      .from('personal_templates')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Failed to update template:', error);
      return null;
    }

    return {
      id: data.id,
      userId: data.user_id,
      name: data.name,
      description: data.description,
      category: data.category as PersonalTemplate['category'],
      icon: data.icon,
      scenarios: data.scenarios as any,
      isPublic: data.is_public,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  }

  // Update in localStorage
  const localTemplates = getLocalTemplates();
  const index = localTemplates.findIndex(t => t.id === id);
  if (index === -1) return null;

  const updated = {
    ...localTemplates[index],
    ...updates,
    name: updates.name?.trim() || localTemplates[index].name,
    description: updates.description?.trim(),
    updatedAt: new Date().toISOString()
  };

  localTemplates[index] = updated;
  saveLocalTemplates(localTemplates);
  return updated;
};

// Sync local templates to cloud when user logs in
export const syncLocalToCloud = async (): Promise<number> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const localTemplates = getLocalTemplates();
  if (localTemplates.length === 0) return 0;

  let syncedCount = 0;

  for (const template of localTemplates) {
    try {
      await supabase.from('personal_templates').insert({
        name: template.name,
        description: template.description,
        category: template.category,
        icon: template.icon,
        scenarios: template.scenarios as any,
        is_public: template.isPublic
      });
      syncedCount++;
    } catch (error) {
      console.error('Failed to sync template:', error);
    }
  }

  if (syncedCount > 0) {
    localStorage.removeItem(STORAGE_KEY);
  }

  return syncedCount;
};
